/**
 * STABILITY-CRITICAL: decide o que é APAGADO do localStorage ao limpar os dados locais ou ao
 * sair. Deny-by-default: chave fora de ALLOWLIST é removida. Antes de alterar, leia
 * docs/llm/slices/sync-engine.md e alinhe com o responsável pelo sync.
 *
 * Grupos: 'session' (some na saída), 'device', 'pref', 'trail' (últimas TRAIL_LIMIT),
 * 'flow' (marcador de limpeza em andamento; some na saída). GIGYA_TEMP_PASSWORD (senha em
 * claro) NUNCA sobrevive. Tabela chave -> fica/sai: docs/superpowers/plans/2026-10-18-entrega3-sair-e-limpeza.md §2.
 */
export type ResetMode = 'limpeza' | 'saida'
export type KeyGroup = 'session' | 'device' | 'pref' | 'trail' | 'flow'

export const CLEANUP_MARKER_KEY = 'progerar.limpeza.marker'
export const TRAIL_LIMIT = 30
export const NEVER_KEEP: readonly string[] = ['GIGYA_TEMP_PASSWORD']

type Rule = { group: KeyGroup; exact?: string; prefix?: string }

export const ALLOWLIST: readonly Rule[] = [
  { group: 'session', exact: 'fallback_token' },
  { group: 'session', exact: 'selectedEstacaoDeMonta' },
  { group: 'device', exact: 'deviceId' },
  { group: 'pref', exact: 'progerar.sidebar.collapsed' },
  { group: 'pref', prefix: 'progerar.rowsPerPage.' },
  { group: 'pref', exact: 'ProGerarPlanilhaConfig' },
  { group: 'pref', exact: 'ProGerarPlanilhaPresets' },
  { group: 'pref', exact: 'importDataTourFinished' },
  { group: 'pref', exact: 'noAlertSyncPull' },
  { group: 'pref', exact: 'reportExportWarning' },
  { group: 'pref', exact: 'ProGerarSavedFieldsToExport' },
  { group: 'pref', prefix: 'ProGerarSavedFieldsToSaveOrPrint' },
  { group: 'pref', exact: 'dialog_2026_2027' },
  { group: 'trail', prefix: 'L - ' },
  { group: 'trail', prefix: 'S - ' },
  { group: 'flow', exact: CLEANUP_MARKER_KEY },
]

function ruleFor(key: string): Rule | undefined {
  return ALLOWLIST.find((r) =>
    r.exact !== undefined
      ? r.exact === key
      : key.startsWith(r.prefix as string),
  )
}

function trailTime(key: string, prefix: string): number {
  const t = Date.parse(key.slice(prefix.length))
  return Number.isNaN(t) ? -Infinity : t
}

export function planPrune(
  keys: readonly string[],
  mode: ResetMode,
  trailLimit: number = TRAIL_LIMIT,
): { keep: string[]; remove: string[] } {
  const keep: string[] = []
  const remove: string[] = []
  const trails: Record<string, string[]> = {}
  for (const key of keys) {
    if (NEVER_KEEP.includes(key)) {
      remove.push(key)
      continue
    }
    const rule = ruleFor(key)
    if (!rule) {
      remove.push(key)
      continue
    }
    if (
      (rule.group === 'session' || rule.group === 'flow') &&
      mode === 'saida'
    ) {
      remove.push(key)
      continue
    }
    if (rule.group === 'trail') {
      const prefix = rule.prefix as string
      if (!trails[prefix]) trails[prefix] = []
      trails[prefix].push(key)
      continue
    }
    keep.push(key)
  }
  Object.keys(trails).forEach((prefix) => {
    const ordered = [...trails[prefix]].sort((a, b) => {
      const ta = trailTime(a, prefix)
      const tb = trailTime(b, prefix)
      return ta === tb ? 0 : ta < tb ? 1 : -1 // mais recente primeiro
    })
    keep.push(...ordered.slice(0, trailLimit))
    remove.push(...ordered.slice(trailLimit))
  })
  return { keep, remove }
}

export type StorageLike = Pick<Storage, 'length' | 'key' | 'removeItem'>

export function pruneLocalStorage(storage: StorageLike, mode: ResetMode) {
  const keys: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i)
    if (k !== null) keys.push(k)
  }
  const plan = planPrune(keys, mode)
  const failed: string[] = []
  for (const key of plan.remove) {
    try {
      storage.removeItem(key)
    } catch {
      failed.push(key)
    }
  }
  return { ...plan, failed }
}
